import React, { useState, useContext, useEffect, useCallback, useRef } from "react";

import { GlobalContext } from "../../../stores/use_global_state";
import MagicBgForm from "../MagicBgForm/MagicBgForm";
import style from "./MagicBgInputBlock.module.scss";
import { saveTextToImageToUserBg, createTextToImg, saveTextToImageToGallery, getTextToImageStyles } from "../../../pages/api/apiTextToImage";
import { addToFavorites } from "../../../pages/api/api";
import { bodyActiveScroll, bodyHiddenScroll } from "../../../utils/bodyToggleHiddenScroll";
import MainBlob from "../../animate/MainBlob";
import useTextToImageStatus from "../../../utils/hooks/useTextToImageStatus";
import { DynamicResultBlock, DynamicStartFavoriteModal, DynamicStartGalleryModal, AuthModal, UnlockResultsModal, QueueComponent, DownLoadModal } from "./dependencies.js";
import * as ga from "../../../lib/ga";
import { gaCreditsLimit } from "../../../utils/gaCreditsLimit";
import { addToLocalStorage, removeFromLocalStorage } from "../../../utils/nonAuthLocalStorage";
import FakeResult from "../FakeResult/FakeResult";
import { defaultSelectedStyle, defaultQueueInfo } from "./constants.js";
import GamesWrapper from "../../games/gamesWrapper.jsx";

// prompt to localStorage -> check auth -> show modal -> auth user enter page -> check storage -> start generate -> clear storage
// a - show on generate click
// b - show on COMPLETED
function MagicBgInputBlock({ dictionary, query }) {
	const { state, dispatch } = useContext(GlobalContext);
	const [isAdaptive, setIsAdaptive] = useState(false);
	const [openStyleSelector, setOpenStyleSelector] = useState(false);
	const [bgSaved, setBgSaved] = useState(false);
	const [stylesList, setStylesList] = useState([]);
	const [isAddedToGallery, setIsAddedToGallery] = useState([]);
	const [isSavedToFavorites, setIsSavedToFavorites] = useState(false);
	const [favoriteStartMessage, setFavoriteStartMessage] = useState(false);
	const [galleryStartMessage, setGalleryStartMessage] = useState(false);
	const [magicBgInfo, setMagicBgInfo] = useState({});
	const [queryStatus, setQueryStatus] = useState("query"); // "query" , "loading", "result"
	const [queueInfo, setQueueInfo] = useState({ ...defaultQueueInfo });
	const [selectedStyle, setSelectedStyle] = useState({ ...defaultSelectedStyle });
	const [downloadedImage, setDownLoadedImage] = useState(null);
	const [showAuthModal, setShowAuthModal] = useState(false);
	const [showFakeResult, setShowFakeResult] = useState(false);
	const [showUnlockModal, setShowUnlockModal] = useState(false);
	const inputRef = useRef(null);
	const descriptionRef = useRef(null);
	const mobileLoadingRef = useRef(null);
	const mobileResultRef = useRef(null);
	const goToEditor = useRef(null);
	const timeoutRef = useTextToImageStatus(dictionary);
	const closeDownLoadModal = () => setDownLoadedImage(null);
	const triggerDownloadModal = (image_url) => setDownLoadedImage(image_url);
	const [showGameModal, setShowGameModal] = useState(false);

	const openAuthHandler = () => {
		setShowAuthModal(bodyHiddenScroll());
		setShowUnlockModal(false);
	};

	const closeAuthHandler = () => setShowAuthModal(bodyActiveScroll());

	const openUnlockResult = () => {
		if (inputRef.current) {
			inputRef.current.scrollIntoView();
		}
		setTimeout(() => {
			setShowFakeResult(true);
		}, 700);
		setTimeout(() => {
			setShowUnlockModal(true);
		}, 1200);
	};

	const closeUnlockResult = () => {
		setShowUnlockModal(false);
	};

	const toggleSelector = () => {
		if (openStyleSelector) {
			setOpenStyleSelector(bodyActiveScroll());
			return;
		}
		setOpenStyleSelector(bodyHiddenScroll());
	};
	const closeSelector = useCallback(
		(e) => {
			if (e?.target?.classList) {
				const classList = [...e?.target?.classList];
				if (classList[1]?.split("_").includes("openStylesBtn")) return;
			}
			if (openStyleSelector) {
				setOpenStyleSelector(bodyActiveScroll());
			}
		},
		//eslint-disable-next-line react-hooks/exhaustive-deps
		[setOpenStyleSelector, openStyleSelector]
	);

	const selectStyle = (styleObj) => {
		ga.event({
			action: `TextToImg page: click '${styleObj.name}'`,
			params: { event_category: "TextToImg page" },
		});
		styleObj.id === selectedStyle?.id ? setSelectedStyle({}) : setSelectedStyle(styleObj);
	};

	const clearState = () => {
		dispatch({ type: "setTextToImgTask", task: null });
		setBgSaved(false);
		setIsAddedToGallery([]);
		setIsSavedToFavorites(false);
		setMagicBgInfo({});
		setOpenStyleSelector(false);
		setFavoriteStartMessage(false);
		setGalleryStartMessage(false);
		setDownLoadedImage(null);
	};

	const createPromptRequest = async (userInput, userLang, extendedSettings, style = null) => {
		if (!state.isAuth) {
			const generateObj = { settings: { ...extendedSettings }, userInput: userInput.trim(), userLang: userLang, style: selectedStyle };
			addToLocalStorage(JSON.stringify(generateObj));
			openUnlockResult();
			return;
		}

		if (state.isAuth) {
			if (state?.accountData?.plan === "free" && state?.accountData?.total_credits < extendedSettings.num_results.value) {
				gaCreditsLimit("IMAGEGEN");
				dispatch({ type: "setVideoError", error_msg: "free_limit_img", error_code: "free_limit_img" });
				return false;
			}

			if ((state?.accountData?.plan === "pro" || state?.accountData?.plan === "promo") && extendedSettings?.accountData?.total_credits < extendedSettings.num_results.value) {
				gaCreditsLimit("IMAGEGEN");
				dispatch({ type: "setVideoError", error_msg: "pro_limit", error_code: "pro_limit" });
				return false;
			}

			if (state.load && state.accountData && state?.accountData?.plan !== "premium") {
				if (state.accountData.total_credits - extendedSettings.num_results.value < 2) {
					dispatch({ type: "setLimitError", error_code: "promotion" });
				}

				dispatch({
					type: "setAccountData",
					accountData: { ...state.accountData, total_credits: state.accountData.total_credits - extendedSettings.num_results.value },
					restrictions: state.restrictions,
				});
			}
		}

		setQueryStatus("loading");

		clearState();
		let token = state?.userInfo?.token ? state.userInfo.token : false;
		const userStyle = style !== null ? style : selectedStyle;
		const params = {
			kind: "IMAGEGEN",
			prompt: userInput.trim(),
			prompt_lang: userLang,
			...(userStyle.id !== 0 && { imagegen_style_id: userStyle.id }),
			...(extendedSettings.negative_prompt !== null && { negative_prompt: extendedSettings.negative_prompt }),
			...(extendedSettings.seed !== null && { seed: extendedSettings.seed }),
			...(extendedSettings.sampler !== null && { sampler: extendedSettings.sampler.id }),
			...(extendedSettings.model !== null && { model: extendedSettings.model.id }),

			steps: extendedSettings.steps,
			out_ar: extendedSettings.out_ar.value,
			num_results: extendedSettings.num_results.value,
			prompt_strength: extendedSettings.prompt_strength,
		};
		// setShowGameModal(true);
		const res = await createTextToImg(token, params);

		if (res.status > 299) {
			setQueryStatus("query");
			alert(dictionary["error_smth_wrong"])
			return;
		}

		removeFromLocalStorage();
		dispatch({ type: "setTextToImgTask", task: res.data, });
	};

	const saveMagicBgToUserBg = async () => {
		const token = state?.userInfo?.token;
		const taskId = state.textToImgTask.id;
		if (token) {
			const { status, data } = await saveTextToImageToUserBg(token, taskId);
			if (status >= 200 && status < 400) {
				setBgSaved(true);
				setMagicBgInfo(data);
				return data;
			}
		}
	};

	const addMagicBgToFavorites = async () => {
		const token = state?.userInfo?.token;

		if (state?.isAuth) {
			if (bgSaved) {
				const props = { id: magicBgInfo.id, token: token };
				addToFavorites(props);
				setIsSavedToFavorites(true);
			} else {
				const data = await saveMagicBgToUserBg();
				setMagicBgInfo(data);
				setBgSaved(true);
				const props = { id: data.id, token: token };
				addToFavorites(props);
				setIsSavedToFavorites(true);
			}
		} else {
			setFavoriteStartMessage(true);
		}
	};

	const addMagicBgToGallery = async (id) => {
		const token = state?.userInfo?.token;

		if (state?.isAuth) {
			const props = { id: id, token: token };
			saveTextToImageToGallery(props);
			setIsAddedToGallery([...isAddedToGallery, id]);
		} else {
			setGalleryStartMessage(true);
		}
	};

	const setQueue = useCallback(() => {
		if (state.textToImgTask !== null) {
			let { queue_place, eta } = state.textToImgTask;

			if (queue_place || eta) {
				setQueueInfo((prev) => {
					return { ...prev, users: queue_place, time: eta };
				});
			}
		}
	}, [state.textToImgTask]);

	useEffect(
		() => {
			if (state.textToImgTask !== null && (state.textToImgTask.queue_place || state.textToImgTask.eta)) {
				setQueueInfo((prev) => {
					return { ...prev, users: state?.queue_place, time: state?.eta };
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[state.textToImgTask]
	);

	useEffect(() => {
		setQueue();
	}, [state.textToImgTask, setQueue]);

	useEffect(() => {
		if (state.textToImgTask?.status === "COMPLETED") {
			setQueryStatus("result");
			ga.event({
				action: `TextToImg page: all images received'`,
				params: { event_category: "TextToImg page" },
			});
		}
		if (state.textToImgTask?.status === "FAILED") {
			setQueryStatus("query");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.textToImgTask?.status]);

	useEffect(() => {
		if (descriptionRef.current !== null && inputRef.current !== null && queryStatus === "loading") {
			window.innerWidth >= 768 ? setTimeout(() => descriptionRef.current.scrollIntoView(), 500) : setTimeout(() => inputRef.current.scrollIntoView(), 500);
		}
		if (inputRef.current !== null && mobileResultRef.current !== null && queryStatus === "result") {
			window.innerWidth >= 768 ? setTimeout(() => inputRef.current.scrollIntoView(), 500) : setTimeout(() => mobileResultRef.current.scrollIntoView(), 500);
		}
	}, [queryStatus, mobileResultRef]);

	useEffect(
		() => {
			const getStylesList = async () => {
				const { data } = await getTextToImageStyles("T2I");
				setStylesList(data);
			};

			const workWithWindowSize = () => {
				closeSelector();

				if (window.innerWidth <= 480) {
					setIsAdaptive(true);
				} else {
					setIsAdaptive(false);
				}
			};
			workWithWindowSize();
			getStylesList();
			window.addEventListener("resize", workWithWindowSize);
			return () => {
				window.removeEventListener("resize", workWithWindowSize);
				if (!goToEditor.current) {
					clearState();
					dispatch({ type: "setTextToImgResults", results: [] });
				}

				if (timeoutRef.current !== null) {
					clearTimeout(timeoutRef.current);
				}
			};
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	useEffect(
		() => {
			if (state.isAuth && state.accountData && !timeoutRef.current) {
				setShowFakeResult(false);
				if (localStorage.nonAuthAi) {
					try {
						JSON.parse(localStorage.nonAuthAi);
					} catch (e) {
						return;
					}
					const preSavedData = JSON?.parse(localStorage.nonAuthAi);
					setSelectedStyle(preSavedData.style);
					removeFromLocalStorage();
					createPromptRequest(preSavedData.userInput, preSavedData.userLang, preSavedData.settings, preSavedData.style);
				}
			}
		},
		//eslint-disable-next-line react-hooks/exhaustive-deps
		[state.accountData, state.isAuth]
	);

	useEffect(
		() => {
			if (query.style !== undefined && stylesList.length > 0) {
				const querySelectedStyle = stylesList.filter((item) => item.name.toLowerCase() === query.style.toLowerCase().replace(/_/g, " "))[0];
				setSelectedStyle(querySelectedStyle);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[stylesList.length, query]
	);

	let title = dictionary["Text-to-image AI generator"];
	let description = dictionary["Realize your ideas — create image from text with AI online"] || "Realize your ideas — create image from text with AI online";

	return (
		<>
			<section className={`${style.block} ${style[queryStatus]}`}>
				<div className={style.text}>
					<h1 ref={mobileLoadingRef} className={style.title}>
						{title}
					</h1>
					<p ref={descriptionRef} className={style.description}>
						{description}
					</p>
				</div>
				<div ref={inputRef} className={style.content}>
					<MagicBgForm
						{...{ selectedStyle, dictionary, isAdaptive, openStyleSelector, selectStyle, stylesList, closeSelector, query }}
						openSelector={toggleSelector}
						isLoading={queryStatus === "loading" || showFakeResult}
						submitUserInput={createPromptRequest}
					/>
					<FakeResult openAuthHandler={openAuthHandler} isVisible={showFakeResult} />
					{queryStatus === "loading" && queryStatus !== "result" ? <QueueComponent queueInfo={queueInfo} dictionary={dictionary} isNotFreeUser={state?.accountData?.plan !== "free"} /> : null}
					{queryStatus === "loading" || queryStatus === "result" ? (
						<div className={style.results} ref={mobileResultRef}>
							<div className={`${style["results-scroll"]} ${state.textToImgResults.length > 1 ? "" : style.odd}`}>
								{state.textToImgResults.map((item, index) => {
									if (item.status !== "COMPLETED") return;
									return (
										<React.Fragment key={item.id}>
											<DynamicResultBlock
												{...{ addMagicBgToGallery, addMagicBgToFavorites, dictionary, isAddedToGallery, isSavedToFavorites, item, isAdaptive, triggerDownloadModal, goToEditor }}
												id={index === 0 ? "test-result" : ""}
											/>
										</React.Fragment>
									);
								})}
							</div>
						</div>
					) : null}
				</div>
				{favoriteStartMessage && <DynamicStartFavoriteModal closeModal={() => setFavoriteStartMessage(false)} dictionary={dictionary} style={style} />}
				{galleryStartMessage && <DynamicStartGalleryModal closeModal={() => setGalleryStartMessage(false)} dictionary={dictionary} style={style} />}
				{downloadedImage ? <DownLoadModal pageName={"TextToImg"} image_url={downloadedImage} closeHandler={closeDownLoadModal} dictionary={dictionary} /> : null}
				<MainBlob isBack={true} />
			</section>

			{showAuthModal && <AuthModal closeHandler={closeAuthHandler} serviceName="TextToImg" testValue={""} />}
			{showUnlockModal && <UnlockResultsModal dictionary={dictionary} openAuthModal={openAuthHandler} closeModal={closeUnlockResult} />}
			{showGameModal && <GamesWrapper close={() => setShowGameModal(false)} task={state.textToImgTask} />}
		</>
	);
}

export default MagicBgInputBlock;
